<template>
  <BaseModal :open="show" :closable="false" size="sm" @close="handleClose">
    <template #header>
      <div class="flex items-center space-x-3">
        <div class="flex-shrink-0">
          <Clock class="h-6 w-6 text-amber-500" />
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Session Expiring Soon</h3>
        </div>
      </div>
    </template>

    <template #body>
      <div class="space-y-4">
        <div class="text-sm text-gray-600">
          <p class="mb-3">
            Your session will expire due to inactivity. You will be automatically logged out in:
          </p>

          <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div class="flex items-center justify-center">
              <div class="text-center">
                <div class="text-3xl font-bold text-amber-600 mb-1">
                  {{ formatTime(timeRemaining) }}
                </div>
                <div class="text-sm text-amber-700">
                  {{ timeRemaining > 60 ? 'minutes' : 'seconds' }} remaining
                </div>
              </div>
            </div>
          </div>

          <p class="mt-3 text-xs text-gray-500">
            Click "Stay Logged In" to extend your session, or "Logout Now" to end your session
            immediately.
          </p>
        </div>

        <!-- Progress bar -->
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div
            class="bg-amber-500 h-2 rounded-full transition-all duration-1000 ease-linear"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end space-x-3">
        <BaseButton variant="outline" size="sm" @click="handleLogout" :disabled="isLoading">
          <LogOut class="h-4 w-4 mr-2" />
          Logout Now
        </BaseButton>

        <BaseButton
          variant="primary"
          size="sm"
          @click="handleExtendSession"
          :disabled="isLoading"
          :loading="isLoading"
        >
          <RefreshCw class="h-4 w-4 mr-2" />
          Stay Logged In
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Clock, LogOut, RefreshCw } from 'lucide-vue-next'
import { BaseModal, BaseButton } from '@/components/ui'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'

interface Props {
  show: boolean
  timeRemaining: number // in seconds
  warningDuration: number // in milliseconds
}

interface Emits {
  (e: 'extend-session'): void
  (e: 'logout'): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const router = useRouter()
const isLoading = ref(false)

// Computed properties
const progressPercentage = computed(() => {
  const warningDurationSeconds = props.warningDuration / 1000
  const elapsed = warningDurationSeconds - props.timeRemaining
  return Math.max(0, Math.min(100, (elapsed / warningDurationSeconds) * 100))
})

// Format time display
const formatTime = (seconds: number): string => {
  if (seconds > 60) {
    const minutes = Math.ceil(seconds / 60)
    return minutes.toString()
  }
  return Math.max(0, Math.ceil(seconds)).toString()
}

// Handle extend session
const handleExtendSession = async () => {
  isLoading.value = true

  try {
    // Emit extend session event
    emit('extend-session')

    // Small delay for UX
    await new Promise((resolve) => setTimeout(resolve, 500))
  } catch (error) {
    console.error('Error extending session:', error)
  } finally {
    isLoading.value = false
  }
}

// Handle logout
const handleLogout = async () => {
  isLoading.value = true

  try {
    emit('logout')
    await authStore.logout()

    router.push({
      path: '/login',
      query: {
        reason: 'manual_logout',
        message: 'You have been logged out successfully.',
      },
    })
  } catch (error) {
    console.error('Error during logout:', error)
    // Force navigation even if logout fails
    router.push('/login')
  } finally {
    isLoading.value = false
  }
}

// Handle modal close (should not be allowed, but included for completeness)
const handleClose = () => {
  // Don't allow closing the modal - user must make a choice
  // emit('close')
}

// Watch for time running out
watch(
  () => props.timeRemaining,
  (newTime) => {
    if (newTime <= 0) {
      // Time's up - the parent component should handle the timeout
      emit('logout')
    }
  },
)
</script>

<style scoped>
/* Custom animations for the progress bar */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Pulsing animation for the time display when time is running low */
@keyframes pulse-amber {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.text-3xl.font-bold.text-amber-600 {
  animation: pulse-amber 1s ease-in-out infinite;
}
</style>
